import { useUrlOpenState } from "@/components/artifacts/useLightboxUrlState.ts";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { Dialog, DialogOverlay, DialogPortal, DialogTrigger } from "@/components/ui/dialog.tsx";
import { getShareTokenFromPath, toSharedAssetPath } from "@/lib/sharedThread.ts";
import { resolveApiPath } from "@/lib/utils.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";

type ImagePreviewComponentData = Extract<ArtifactComponent, { type: "imagePreview" }>;

/** Query param that holds the open viewer's token, apart from `lightbox` so the
 *  viewer can open over an artifact lightbox without closing it. */
const IMAGE_VIEWER_PARAM = "image";

export const ImagePreviewBlock = ({ component }: { component: ImagePreviewComponentData }) => {
  const { artifactId, isSharedThread, maximized } = useArtifactViewCtx();
  const [viewerOpen, setViewerOpen] = useUrlOpenState(
    IMAGE_VIEWER_PARAM,
    `${artifactId}:${component.id}`,
  );
  if (isSharedThread && component.sharedVisibility === "omit") return null;

  const toViewable = (path: string): string | null => {
    if (!(component.requiresAuth && isSharedThread)) return resolveApiPath(path);
    // The session-authed endpoint 401s for an unauthed shared-thread viewer, so
    // rewrite to the public share-token-scoped path (authorized by snapshot
    // membership server-side). No token → can't authorize, so omit the image.
    const shareToken = getShareTokenFromPath();
    return shareToken ? resolveApiPath(toSharedAssetPath(path, shareToken)) : null;
  };

  // Inline cards use the smaller `src`; the artifact lightbox and the
  // full-screen viewer upgrade to `srcLarge`.
  const src = toViewable(maximized && component.srcLarge ? component.srcLarge : component.src);
  const largeSrc = toViewable(component.srcLarge ?? component.src);
  if (src === null || largeSrc === null) return null;

  return (
    <ImageViewer open={viewerOpen} onOpenChange={setViewerOpen} src={largeSrc} alt={component.alt}>
      <button
        type="button"
        aria-label={`View ${component.alt} full screen`}
        className="block w-full cursor-zoom-in overflow-hidden rounded border bg-background"
      >
        <img src={src} alt={component.alt} className="max-w-full h-auto block" />
      </button>
    </ImageViewer>
  );
};

/**
 * The picture alone over a dark scrim, fitted to the window. Escape, the close
 * button, or a click anywhere off the picture dismisses it.
 *
 * `children` is the opener. It renders as the dialog's trigger so Radix hands
 * keyboard focus back to it when the viewer closes.
 */
const ImageViewer = ({
  open,
  onOpenChange,
  src,
  alt,
  children,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  src: string;
  alt: string;
  children: ReactNode;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>{children}</DialogTrigger>
    <DialogPortal>
      <DialogOverlay className="bg-black/85 backdrop-blur-none" />
      <DialogPrimitive.Content
        aria-describedby={undefined}
        // The content fills the viewport, so Radix never sees a click outside
        // it. A click that lands on the content itself, not the picture or the
        // close button, is a click on the scrim.
        onClick={(e) => {
          if (e.target === e.currentTarget) onOpenChange(false);
        }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-hidden md:p-12 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
      >
        <DialogPrimitive.Title className="sr-only">{alt}</DialogPrimitive.Title>
        <img
          src={src}
          alt={alt}
          className="max-h-full max-w-full rounded object-contain shadow-2xl"
        />
        <DialogPrimitive.Close
          aria-label="Close image"
          className="absolute right-4 top-4 rounded-full bg-black/50 p-2 text-white/80 transition-colors hover:bg-black/70 hover:text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white"
        >
          <X className="h-5 w-5" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  </Dialog>
);
